import { createContext, memo, type ReactNode, useContext, useRef } from 'react';
import invariant from 'tiny-invariant';
import { createStore, type StoreApi, useStore } from 'zustand';
import { persist } from 'zustand/middleware';

import { reportErrorToBugsnag } from '@air/utils-error';

import { type CollapseSlice, createCollapseSlice } from '~/components/v2/store/shared/createCollapseSlice';
import { createTreeSlice, type TreeSlice } from '~/components/v2/store/shared/createTreeSlice';
import { type ZustandStorageKeys } from '~/constants/zustandStorageKeys';

export type WorkspaceSidebarSectionStoreState = CollapseSlice & TreeSlice;

export const WorkspaceSidebarSectionStoreContext = createContext<StoreApi<WorkspaceSidebarSectionStoreState> | null>(
  null,
);

export type WorkspaceSidebarSectionStoreContextProviderProps = {
  children: ReactNode;
  /**
   * Initial collapsed state when nothing is stored yet. Persisted toggles win after that.
   */
  defaultCollapsed?: boolean;
  storageKey: ZustandStorageKeys;
};

export const WorkspaceSidebarSectionStoreContextProvider = memo(
  ({ children, defaultCollapsed = false, storageKey }: WorkspaceSidebarSectionStoreContextProviderProps) => {
    const storeRef = useRef<StoreApi<WorkspaceSidebarSectionStoreState>>();

    invariant(storageKey, 'storageKey is required');

    storeRef.current ??= createStore<WorkspaceSidebarSectionStoreState>()(
      persist(
        (...args) => ({
          ...createCollapseSlice({ collapsed: defaultCollapsed })(...args),
          ...createTreeSlice({ treeItems: [] })(...args),
        }),
        {
          name: storageKey,
          partialize: (state) => ({
            collapsed: state.collapsed,
          }),
          version: 1,
        },
      ),
    );

    return (
      <WorkspaceSidebarSectionStoreContext.Provider value={storeRef.current}>
        {children}
      </WorkspaceSidebarSectionStoreContext.Provider>
    );
  },
);

WorkspaceSidebarSectionStoreContextProvider.displayName = 'WorkspaceSidebarSectionStoreContextProvider';

export const useWorkspaceSidebarSectionStore = <T,>(selector: (store: WorkspaceSidebarSectionStoreState) => T): T => {
  const store = useContext(WorkspaceSidebarSectionStoreContext);

  if (!store) {
    const context = 'useWorkspaceSidebarSectionStore must be used within a WorkspaceSidebarSectionStoreContextProvider';
    const error = new Error(context);

    reportErrorToBugsnag({
      error,
      context,
    });

    throw error;
  }

  return useStore(store, selector);
};
